import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import axios from 'axios';
import { Mail, MapPin, Phone, Calendar, MessageSquare } from 'lucide-react';
import { getPartners } from '../utils/api';
import MathCaptcha from '../components/MathCaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [activeTab, setActiveTab] = useState('contact');
  const [partners, setPartners] = useState([]);
  const [contactCaptchaVerified, setContactCaptchaVerified] = useState(false);
  
  // Contact Form State
  const [contactData, setContactData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [contactStatus, setContactStatus] = useState({ type: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);

  // Fetch partners on mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const data = await getPartners();
        setPartners(data);
      } catch (error) {
        console.error('Error fetching partners:', error);
      }
    };
    fetchPartners();
  }, []);

  const handleContactChange = (e) => {
    setContactData({ ...contactData, [e.target.name]: e.target.value });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactCaptchaVerified) {
      setContactStatus({ 
        type: 'error', 
        message: language === 'de' ? 'Bitte lösen Sie die Sicherheitsfrage' : 'Please solve the security question' 
      });
      return;
    }
    
    setContactLoading(true);
    setContactStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, {
        ...contactData,
        language
      });
      setContactStatus({ type: 'success', message: t('contact.success') });
      setContactData({ name: '', email: '', subject: '', message: '' });
      setContactCaptchaVerified(false);
    } catch (error) {
      setContactStatus({ type: 'error', message: t('contact.error') });
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero with Map Background */}
      <section className="relative text-white py-20 overflow-hidden">
        {/* Google Maps Background */}
        <div className="absolute inset-0 w-full h-full">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3561.0875446789!2d33.9489919!3d26.8399481!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x144d71b8c6955555%3A0xc2cd48372d05a9a!2sSurf-%20und%20Kitemotion%20Peter%20M%C3%BCller!5e1!3m2!1sde!2seg!4v1698765432123!5m2!1sde!2seg"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Surfmotion Background Map"
            className="pointer-events-none"
          ></iframe>
        </div>
        {/* Dark Overlay - not too dark */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/70 to-blue-900/70"></div>
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Mail className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {language === 'de' ? 'Kontakt & Registrierung' : 'Contact & Registration'}
          </h1>
          <p className="text-xl text-cyan-100">
            {language === 'de' 
              ? 'Nehmen Sie Kontakt auf oder registrieren Sie sich vorab für Ihren Kurs' 
              : 'Get in touch or pre-register for your course'}
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('contact')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'contact'
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-5 h-5" />
                <span>{language === 'de' ? 'Kontakt' : 'Contact'}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('booking')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'booking'
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>{language === 'de' ? 'Vorab-Registrierung' : 'Pre-Registration'}</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Forms */}
            <div>
              {activeTab === 'contact' ? (
                <div>
                  <h2 className="text-2xl font-bold mb-6">{t('contact.title')}</h2>
                  <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.name')}</label>
                      <input
                        type="text"
                        name="name"
                        data-testid="contact-name"
                        value={contactData.name}
                        onChange={handleContactChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.email')}</label>
                      <input
                        type="email"
                        name="email"
                        data-testid="contact-email"
                        value={contactData.email}
                        onChange={handleContactChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.subject')}</label>
                      <input
                        type="text"
                        name="subject"
                        data-testid="contact-subject"
                        value={contactData.subject}
                        onChange={handleContactChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('contact.message')}</label>
                      <textarea
                        name="message"
                        data-testid="contact-message"
                        value={contactData.message}
                        onChange={handleContactChange}
                        required
                        rows={6}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    
                    {/* Math Captcha */}
                    <MathCaptcha onVerify={setContactCaptchaVerified} language={language} />
                    
                    {contactStatus.message && (
                      <div
                        data-testid="contact-status"
                        className={`p-4 rounded-lg ${
                          contactStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                        }`}
                      >
                        {contactStatus.message}
                      </div>
                    )}
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={contactLoading}
                      className="w-full bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {contactLoading ? t('contact.sending') : t('contact.send')}
                    </button>
                  </form>
                </div>
              ) : (
                <div>
                  <h2 className="text-2xl font-bold mb-6">
                    {language === 'de' ? 'Vorab-Registrierung' : 'Pre-Registration'}
                  </h2>
                  
                  {/* Important Notice Box */}
                  <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-r-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-amber-800 font-semibold">
                          {language === 'de' ? 'Dies ist KEINE verbindliche Buchung!' : 'This is NOT a binding booking!'}
                        </h3>
                        <p className="text-amber-700 mt-1 text-sm">
                          {language === 'de' 
                            ? 'Mit dieser Vorab-Registrierung sparst du Zeit vor Ort. Dokumente und Daten kannst du hier schon hinterlegen für einen schnelleren Check-in bei Ankunft.' 
                            : 'With this pre-registration you save time on site. You can already upload documents and data here for a faster check-in on arrival.'}
                        </p>
                        <p className="text-amber-800 mt-3 text-sm font-medium">
                          {language === 'de' 
                            ? '👉 Für verbindliche Buchungen kontaktiere uns direkt:' 
                            : '👉 For binding bookings, contact us directly:'}
                        </p>
                        <p className="text-amber-700 text-sm">
                          📧 peter@surfmotion.com | 📞 +20 106 856 2062
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">
                    {language === 'de' 
                      ? 'Nutzen Sie unser Online-System für eine schnelle Vorab-Registrierung:' 
                      : 'Use our online system for quick pre-registration:'}
                  </p>
                  
                  {/* External Reservation System */}
                  <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg">
                    <iframe
                      src="https://reservations.surfmotion.com/reservation"
                      width="100%"
                      height="700"
                      style={{ border: 0 }}
                      title="Surfmotion Reservation System"
                      className="bg-white"
                    ></iframe>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">{t('contact.info')}</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.address')}</h3>
                    <p className="text-gray-600">
                      Surf- und Kitemotion Peter Müller<br />
                      Palm Royale Soma Bay<br />
                      Red Sea, Hurghada<br />
                      Egypt
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.phone')}</h3>
                    <p className="text-gray-600">Mobile: +20 106 856 2062</p>
                    <p className="text-sm text-amber-600 mt-1">
                      {language === 'de' 
                        ? 'WhatsApp CALL nicht möglich' 
                        : 'WhatsApp CALL not available'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-cyan-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{t('contact.email_label')}</h3>
                    <p className="text-gray-600">peter@surfmotion.com</p>
                    <p className="text-sm text-cyan-600 mt-1">
                      {language === 'de' 
                        ? 'Reservierung/Anfragen nur per E-Mail' 
                        : 'Reservations/Inquiries by email only'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Opening Hours or Additional Info */}
              <div className="mt-8 p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-3">
                  {language === 'de' ? 'Öffnungszeiten' : 'Opening Hours'}
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p className="flex justify-between">
                    <span>{language === 'de' ? 'Oktober - April' : 'October - April'}</span>
                    <span className="font-medium">9:00 - 17:00</span>
                  </p>
                  <p className="flex justify-between">
                    <span>{language === 'de' ? 'Mai - September' : 'May - September'}</span>
                    <span className="font-medium">9:00 - 18:00</span>
                  </p>
                </div>
              </div>

              {/* Google Maps */}
              <div className="mt-8">
                <h3 className="font-semibold text-lg mb-4">
                  {language === 'de' ? 'Unsere Location' : 'Our Location'}
                </h3>
                <div className="rounded-lg overflow-hidden shadow-lg border border-gray-200">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3561.0875446789!2d33.9489919!3d26.8399481!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x144d71b8c6955555%3A0xc2cd48372d05a9a!2sSurf-%20und%20Kitemotion%20Peter%20M%C3%BCller!5e1!3m2!1sde!2seg!4v1698765432123!5m2!1sde!2seg"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Surfmotion Location Map"
                  ></iframe>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  {language === 'de' 
                    ? 'Surf- und Kitemotion, Palm Royale Resort, Soma Bay, Rotes Meer, Ägypten'
                    : 'Surf- and Kitemotion, Palm Royale Resort, Soma Bay, Red Sea, Egypt'}
                </p>
                <a 
                  href="https://www.google.com/maps/place/Surf-+und+Kitemotion+Peter+M%C3%BCller/@26.84013,33.951162,978m/data=!3m1!1e3!4m6!3m5!1s0x144d71b8c6955555:0xc2cd48372d05a9a!8m2!3d26.8399481!4d33.9511669!16s%2Fg%2F11cn6dk40c?hl=de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-cyan-600 hover:text-cyan-800 text-sm mt-2"
                >
                  {language === 'de' ? 'In Google Maps öffnen' : 'Open in Google Maps'}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      {partners.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {language === 'de' ? 'Partner' : 'Partners'}
              </h2>
              <p className="text-xl text-gray-600">
                {language === 'de' 
                  ? 'Vertrauen von Top-Unternehmen rund um den Globus' 
                  : 'Trusted by top companies around the globe'}
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
              {partners.map((partner) => (
                <a
                  key={partner.id}
                  href={partner.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg p-6 flex items-center justify-center hover:shadow-xl transition-shadow duration-300 group"
                >
                  <img
                    src={partner.logo_url}
                    alt="Partner logo"
                    className="max-w-full max-h-20 object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Contact;